import type { MusicDictionary, MusicGenre, MusicMood } from '../music/types';
import type { MapBucket } from './api';

const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://navi-vibe.com';
const BASE = `${API_BASE}/api/music`;

export async function fetchDictionary(currentVersion: number): Promise<MusicDictionary | null> {
    const res = await fetch(`${BASE}/dictionary?v=${currentVersion}`, {
        credentials: 'include',
        headers: currentVersion > 0 ? { 'If-None-Match': `"v${currentVersion}"` } : {},
    });
    if (res.status === 304) return null;
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`dictionary fetch failed: ${res.status}`);
    return (await res.json()) as MusicDictionary;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
    if (res.ok) return (await res.json()) as T;
    let err = `http_${res.status}`;
    try {
        const body = await res.json() as { err?: string };
        if (body.err) err = body.err;
    } catch { /* noop */ }
    throw new Error(err);
}

export interface MusicSampleBody {
    h3: string;
    genre: MusicGenre;
    mood?: MusicMood;
    drive_state?: string;
    time_bucket?: string;
}

export async function postMusicSample(body: MusicSampleBody): Promise<{ written: boolean; reason?: string }> {
    const res = await fetch(`${BASE}/sample`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
}

export async function fetchNearbySameGenre(args: {
    h3: string;
    genre: MusicGenre;
}): Promise<{ present: boolean }> {
    const params = new URLSearchParams({ h3: args.h3, genre: args.genre });
    const res = await fetch(`${BASE}/nearby-same-genre?${params}`, { credentials: 'include' });
    return jsonOrThrow(res);
}

export interface MusicCell {
    h3: string;
    centroid: { lat: number; lon: number };
    dominant_genre: MusicGenre | 'mixed';
    dominant_mood: MusicMood | null;
    dominant_share: number;
    distinct_k: number;
    locality_label: string | null;
}

export async function fetchMusicCells(args: {
    west: number;
    south: number;
    east: number;
    north: number;
    bucket: MapBucket;
    genre?: MusicGenre;
}): Promise<{ cells: MusicCell[]; snapshot_run_at: number | null }> {
    const params = new URLSearchParams({
        west: String(args.west),
        south: String(args.south),
        east: String(args.east),
        north: String(args.north),
        bucket: args.bucket,
    });
    if (args.genre) params.set('genre', args.genre);
    const res = await fetch(`${API_BASE}/api/map/music-cells?${params}`, { credentials: 'include' });
    return jsonOrThrow(res);
}
